import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { useUiLang } from "../../stores/uiLang";

// Run E2 J2: FAQ rewritten for Project Explore (K-5), English and Spanish.
// The old answers described the PFL Academy high-school course. Facts here come
// from context/PE_RULES_FOR_CC.md and from the app (teacher dashboard, Class
// Management, login page).
type QA = { q: string; a: ReactNode };

const linkCls = "text-indigo-700 underline";

const FAQ_T: Record<"en" | "es", { title: string; intro: string; items: QA[] }> = {
  en: {
    title: "Frequently Asked Questions",
    intro: "Quick answers to common questions from teachers and administrators.",
    items: [
      {
        q: "What is Project Explore?",
        a: "Project Explore is a K-5 literacy program in English and Spanish. Students read and listen to stories, and financial literacy is what the stories are about. The Spanish is written natively, not translated word for word.",
      },
      {
        q: "How is the program organized?",
        a: "There are four seasons with twelve chapters each. Every chapter has a story episode of about ten minutes at four tiers, in English and Spanish, plus one extension activity. The tiers are T1 (K and 1st grade), T2 (2nd grade), T3 (3rd grade) and T4 (4th and 5th grade).",
      },
      {
        q: "How does it fit into my literacy block?",
        a: "In kindergarten to 2nd grade, Project Explore is a supplement. In 3rd to 5th grade, it can be the main program.",
      },
      {
        q: "How do I get started?",
        a: (
          <>
            Your teacher account is set up for your school by an administrator.
            In your teacher dashboard, create a class, add your students, then
            choose Assign PE Chapter. The{" "}
            <Link to="/support/help-center" className={linkCls}>
              Help Center
            </Link>{" "}
            has a short guide for each step.
          </>
        ),
      },
      {
        q: "Do students need accounts?",
        a: "Yes. You add students in Class Management by typing them in, uploading a CSV or Excel file, or syncing with Google Classroom. Students sign in with Google, with Clever, or with an email and password.",
      },
      {
        q: "Can I track student progress and grades?",
        a: "Yes. The teacher dashboard has a Gradebook and a Student Progress page for each class. Answers that can be checked automatically are scored for you.",
      },
      {
        q: "Which standards does it cover?",
        a: "Project Explore covers six national standards for personal finance education from CEE and Jump$tart.",
      },
      {
        q: "Does it work with our learning management system?",
        a: "Teachers can sync classes from Google Classroom, students can sign in with Clever, and Canvas schools can connect through LTI.",
      },
      {
        q: "What support is available for teachers?",
        a: (
          <>
            The{" "}
            <Link to="/support/help-center" className={linkCls}>
              Help Center
            </Link>{" "}
            has short guides. You can also ask the support chat, send us
            feedback, or email hello@startupsmartup.com.
          </>
        ),
      },
    ],
  },
  es: {
    title: "Preguntas frecuentes",
    intro: "Respuestas rápidas a preguntas comunes de maestros y administradores.",
    items: [
      {
        q: "¿Qué es Project Explore?",
        a: "Project Explore es un programa de lectoescritura de K a 5.º grado en inglés y español. Los estudiantes leen y escuchan cuentos, y los cuentos tratan de educación financiera. El texto en español se escribió directamente en español. No es una traducción palabra por palabra.",
      },
      {
        q: "¿Cómo está organizado el programa?",
        a: "Hay cuatro temporadas con doce capítulos cada una. Cada capítulo tiene un episodio de unos diez minutos en cuatro niveles, en inglés y en español, y una actividad de extensión. Los niveles son T1 (kínder y 1.er grado), T2 (2.º grado), T3 (3.er grado) y T4 (4.º y 5.º grado).",
      },
      {
        q: "¿Cómo encaja en mi bloque de lectoescritura?",
        a: "De kínder a 2.º grado, Project Explore es un complemento. De 3.º a 5.º grado, puede ser el programa principal.",
      },
      {
        q: "¿Cómo empiezo?",
        a: (
          <>
            Un administrador crea tu cuenta de maestro para tu escuela. En tu
            panel de maestro, crea una clase, agrega a tus estudiantes y luego
            elige Asignar un capítulo. El{" "}
            <Link to="/support/help-center" className={linkCls}>
              Centro de ayuda
            </Link>{" "}
            tiene una guía breve para cada paso.
          </>
        ),
      },
      {
        q: "¿Los estudiantes necesitan una cuenta?",
        a: "Sí. Agregas a los estudiantes en Administrar clases: los escribes uno por uno, subes un archivo CSV o de Excel, o sincronizas con Google Classroom. Los estudiantes inician sesión con Google, con Clever o con un correo electrónico y una contraseña.",
      },
      {
        q: "¿Puedo ver el progreso y las calificaciones de mis estudiantes?",
        a: "Sí. El panel de maestro tiene una página de calificaciones y una página de progreso para cada clase. Las respuestas que se pueden revisar de forma automática se califican solas.",
      },
      {
        q: "¿Qué estándares cubre?",
        a: "Project Explore cubre seis estándares nacionales de educación financiera personal de CEE y Jump$tart.",
      },
      {
        q: "¿Funciona con nuestro sistema de gestión del aprendizaje?",
        a: "Los maestros pueden sincronizar sus clases desde Google Classroom, los estudiantes pueden iniciar sesión con Clever y las escuelas que usan Canvas se pueden conectar por LTI.",
      },
      {
        q: "¿Qué ayuda hay para los maestros?",
        a: (
          <>
            El{" "}
            <Link to="/support/help-center" className={linkCls}>
              Centro de ayuda
            </Link>{" "}
            tiene guías breves. También puedes usar el chat de ayuda, enviarnos
            comentarios o escribir a hello@startupsmartup.com.
          </>
        ),
      },
    ],
  },
};

export default function FAQPage() {
  const t = FAQ_T[useUiLang()];
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-16 sm:py-20 text-white text-center px-4">
        <h1 className="text-4xl font-bold mb-4">{t.title}</h1>
        <p className="text-lg max-w-2xl mx-auto">{t.intro}</p>
      </section>

      {/* FAQ Content */}
      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10">
        {t.items.map((item) => (
          <div key={item.q}>
            <h2 className="text-xl font-semibold text-indigo-700 mb-2">
              {item.q}
            </h2>
            <p className="text-gray-700 text-sm">{item.a}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
